<script setup lang="ts">
const props = defineProps<{
  text: StoryBlokRichText
  icon: StoryBlokIcon
  tags: string
  title: string
  arrowText?: string
  type: 'large' | 'small' | 'arrow'
  orientation: 'left' | 'right'
  variant: 'normal' | 'warning'
  verticalAlignment?: 'items-start' | 'items-center' | 'items-end'
  image: StoryBlokImage
}>()

const tags = props.tags.split(',')
</script>

<template>
  <div class="mx-auto w-full">
    <div class="rounded-xl relative border-1 flex overflow-hidden" :class="[orientation, type, variant, verticalAlignment || 'normal']">
      <div v-if="(image.filename || icon.icon) && type === 'large'" class="image">
        <StoryblokImage :src="image" height="320" width="580" mheight="320" mwidth="600" />
      </div>
      <div v-else-if="image.filename || icon.icon" class="image">
        <div v-if="!image.filename" class="icon-wrapper">
          <AppIcon size="80" msize="40" class="text-primary" v-bind="icon" />
        </div>
        <div v-else>
          <StoryblokImage :src="image" height="200" width="200" mheight="150" mwidth="150" />
        </div>
      </div>
      <div v-if="type === 'arrow'" class="image">
        <div class="flex h-full items-center justify-center">
          <span class="headline-2 -ml-3 text-white md:pl-2">{{ arrowText }}</span>
          <div class="triangle" />
        </div>
      </div>
      <div class="p-5">
        <div v-if="tags" class="flex gap-1 mb-10px <md:max-w-3/5">
          <span v-for="tag in tags" class="bg-primary rounded-lg text-white px-10px font-semibold text-small mt-3">{{ tag }}</span>
        </div>
        <h3 :class="[icon.icon ? 'min-h-80px pb-2.5 w-2/3 sm:min-h-0 sm:w-auto' : 'mb-2.5']">
          {{ title ?? '' }}
        </h3>
        <StoryblokRichText :document="text" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.arrow {
  @apply bg-white flex-col md:max-h-188px;
  & > .image {
    @apply bg-primary p-2.5 relative md:mr-50px md:max-w-197px;
  }
}
.triangle {
  @apply absolute border-transparent w-0 h-0
  md:border-t-94px md:border-b-94px md:border-l-50px md:-right-12.5 md:border-l-primary;
}
.large {
  @apply flex-col bg-primary-pastel;
  & > .image {
    @apply min-w-1/2 bg-no-repeat sm:h-auto;
  }
  & > .text {
    @apply min-w-1/2;
  }
}
.small {
  @apply flex-col-reverse bg-primary-pastel;
  & > .image {
    @apply sm:pt-5 flex justify-center min-w-max;
  }
  & > .text {
    @apply w-full;
  }
  &.left {
    & > .image {
      @apply pl-5;
    }
  }
  &.right {
    & > .image {
      @apply pr-5;
    }
  }
}

.left {
  @apply sm:flex-row;
}
.right {
  @apply justify-between sm:flex-row-reverse;
}
.icon-wrapper {
  @apply absolute top-5 right-5 h-80px w-80px mb-5 bg-primary rounded-full flex items-center justify-center bg-opacity-15
  sm: (h-120px w-120px static);
}
.normal {
  @apply border-primary;
}
.warning {
  @apply border-[#E18F15] bg-[#FAF6E2];
}
</style>
